import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
    FiArrowLeft, FiPlus, FiEdit2, FiTrash2,
    FiCalendar, FiUser, FiAlertCircle, FiCheckSquare, FiMoreVertical, 
    FiUsers, FiUserPlus, FiX, FiSearch, FiFilter, FiChevronDown, FiFolder
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { normalizeRole } from '../../finance/auth/permissions';
import { fmtDate } from '../../utils/istDate';
import ConfirmModal from '../shared/ConfirmModal';
import CreateTaskModal from '../MySpace/CreateTaskModal';
import TaskChecklistIcon from '../shared/TaskChecklistIcon';
import { STATUS_META, PRIORITY_META, STATUS_FILTER_OPTIONS, PRIORITY_FILTER_OPTIONS } from '../MySpace/MySpaceTasks';
import tasksApi, { canEditTask, canChangeTaskStatus } from '../../api/tasksApi';
import api from '../../finance/api/client';

export default function ProjectDetails() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const currentUsername = localStorage.getItem('username') || '';
    const role = localStorage.getItem('role') || '';
    const normalizedRole = normalizeRole(role);
    const isAdmin = normalizedRole === 'ADMIN' || normalizedRole === 'SUPERADMIN' || normalizedRole === 'TESTER';

    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [users, setUsers] = useState([]);
    const [projectUsers, setProjectUsers] = useState([]);
    const [assignUsername, setAssignUsername] = useState('');

    // Project Edit Modal & Team Drawer State
    const [showEditProjectModal, setShowEditProjectModal] = useState(false);
    const [isTeamDrawerOpen, setIsTeamDrawerOpen] = useState(false);
    const [projectFormData, setProjectFormData] = useState({
        projectCode: '',
        projectName: '',
        description: '',
        status: 'ACTIVE',
        clientName: '',
        projectManagerId: '',
        budget: '',
        startDate: '',
        endDate: '',
        productId: ''
    });
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, loading: false });

    // Task Create / Edit Modal State
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);

    // Filter Lists
    const [projectsList, setProjectsList] = useState([]);
    const [productsList, setProductsList] = useState([]);

    // Filters matching Tasks view
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [priorityFilter, setPriorityFilter] = useState('ALL');
    const [teamFilter, setTeamFilter] = useState('ALL');
    const [employeeFilter, setEmployeeFilter] = useState('ALL');
    const [projectFilter, setProjectFilter] = useState(String(projectId));
    const [productFilter, setProductFilter] = useState('ALL');

    // Filter Popover State
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterBtnRef = useRef(null);
    const filterPopoverRef = useRef(null);
    const [popoverPos, setPopoverPos] = useState({ top: 0, right: 0 });

    const updatePopoverPos = useCallback(() => {
        if (!filterBtnRef.current) return;
        const rect = filterBtnRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const popoverHeight = 360;
        let top = rect.bottom + 6;
        if (spaceBelow < popoverHeight && rect.top > popoverHeight) {
            top = Math.max(10, rect.top - popoverHeight - 6);
        }
        const right = Math.max(10, window.innerWidth - rect.right);
        setPopoverPos({ top, right });
    }, []);

    useEffect(() => {
        if (isFilterOpen) {
            updatePopoverPos();
            const handleScrollOrResize = () => updatePopoverPos();
            window.addEventListener('resize', handleScrollOrResize);
            window.addEventListener('scroll', handleScrollOrResize, true);
            return () => {
                window.removeEventListener('resize', handleScrollOrResize);
                window.removeEventListener('scroll', handleScrollOrResize, true);
            };
        }
    }, [isFilterOpen, updatePopoverPos]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setIsFilterOpen(false);
                setIsTeamDrawerOpen(false);
                setShowEditProjectModal(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                filterPopoverRef.current && 
                !filterPopoverRef.current.contains(e.target) &&
                filterBtnRef.current &&
                !filterBtnRef.current.contains(e.target)
            ) {
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch Project Details
    const fetchProject = useCallback(async () => {
        try { 
            const r = await api.get(`/project/${projectId}`); 
            const data = r.data?.data || r.data;
            setProject(data); 
            if (data?.productId) {
                setProductFilter(String(data.productId));
            }
        } catch (e) {
            console.error('Failed to load project details:', e);
        }
    }, [projectId]);

    // Fetch Project Team Members
    const fetchProjectUsers = useCallback(async () => {
        try {
            const r = await api.get(`/project/users/all?projectId=${projectId}`);
            const raw = r.data?.data || r.data;
            setProjectUsers(Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []));
        } catch (e) {
            console.error('Failed to load project team:', e);
        }
    }, [projectId]);

    // Load available projects and products for filtering
    useEffect(() => {
        tasksApi.getProjects().then(res => setProjectsList(Array.isArray(res) ? res : [])).catch(() => {});
        tasksApi.getProducts().then(res => setProductsList(Array.isArray(res) ? res : [])).catch(() => {});
    }, []);

    // Fetch Tasks according to project / product filter selection
    const fetchTasks = useCallback(async () => {
        setLoading(true); setError('');
        try {
            let taskData = [];
            if (projectFilter && projectFilter !== 'ALL') {
                taskData = await tasksApi.getProjectTasks(projectFilter);
            } else if (productFilter && productFilter !== 'ALL') {
                taskData = await tasksApi.getProductTasks(productFilter);
            } else {
                taskData = await tasksApi.getProjectTasks(projectId);
            }
            setTasks(Array.isArray(taskData) ? taskData : []);
        } catch (e) { 
            setError('Failed to load tasks.'); 
        } finally { 
            setLoading(false); 
        }
    }, [projectFilter, productFilter, projectId]);

    useEffect(() => { 
        fetchProject(); 
        fetchTasks(); 
        fetchProjectUsers();
        if (isAdmin) {
            api.get('/admin/users').then(r => {
                const raw = r.data?.data || r.data;
                setUsers(Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []));
            }).catch(() => {});
        } else {
            tasksApi.getAssignees().then(assignees => {
                if (Array.isArray(assignees) && assignees.length > 0) {
                    setUsers(prev => prev.length > 0 ? prev : assignees);
                }
            }).catch(() => {});
        }
        tasksApi.getProducts().then(prods => {
            if (Array.isArray(prods)) setProductsList(prods);
        }).catch(() => {});
    }, [fetchProject, fetchTasks, fetchProjectUsers, isAdmin]);

    // Extract unique teams / departments from project team, users, and tasks (same as Tasks view)
    const uniqueTeams = useMemo(() => {
        const set = new Set();
        (projectUsers || []).forEach(u => {
            if (u.department && u.department.trim()) set.add(u.department.trim());
        });
        (users || []).forEach(u => {
            if (u.department && u.department.trim()) set.add(u.department.trim());
        });
        (tasks || []).forEach(t => {
            if (t.department && t.department.trim()) set.add(t.department.trim());
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [projectUsers, users, tasks]);

    // Extract unique employees / assignees (filtered by selected team if teamFilter !== 'ALL')
    const uniqueEmployees = useMemo(() => {
        const map = new Map();
        (projectUsers || []).forEach(u => {
            if (u.username) {
                map.set(u.username, {
                    username: u.username,
                    fullName: u.fullName || u.username,
                    department: u.department || ''
                });
            }
        });
        (users || []).forEach(u => {
            if (u.username && !map.has(u.username)) {
                map.set(u.username, {
                    username: u.username,
                    fullName: u.fullName || u.username,
                    department: u.department || ''
                });
            }
        });
        (tasks || []).forEach(t => {
            const uname = t.username || t.assigneeId;
            if (uname && !map.has(uname)) {
                map.set(uname, {
                    username: uname,
                    fullName: t.assigneeName || uname,
                    department: t.department || ''
                });
            }
        });
        let list = Array.from(map.values());
        if (teamFilter !== 'ALL') {
            list = list.filter(e => (e.department || '').toUpperCase() === teamFilter.toUpperCase());
        }
        return list.sort((a, b) => (a.fullName || a.username).localeCompare(b.fullName || b.username));
    }, [projectUsers, users, tasks, teamFilter]);

    // Filter tasks locally by status, priority, team, employee, and search
    const filteredTasks = useMemo(() => {
        return tasks.filter(t => {
            // Search query filter
            if (search.trim()) {
                const q = search.toLowerCase();
                const matchTitle = (t.title || '').toLowerCase().includes(q);
                const matchDesc = (t.description || '').toLowerCase().includes(q);
                const matchAssignee = (t.assigneeName || t.username || '').toLowerCase().includes(q);
                const matchProject = (t.projectName || '').toLowerCase().includes(q);
                const matchProduct = (t.productName || '').toLowerCase().includes(q);
                if (!matchTitle && !matchDesc && !matchAssignee && !matchProject && !matchProduct) return false;
            }

            // Status filter
            if (statusFilter !== 'ALL') {
                if (statusFilter === 'OVERDUE') {
                    const isOverdue = Boolean(
                        t.deadLine && 
                        t.taskStatus !== 'COMPLETED' && 
                        new Date(t.deadLine).setHours(0,0,0,0) < new Date().setHours(0,0,0,0)
                    );
                    if (!isOverdue) return false;
                } else if (t.taskStatus !== statusFilter) {
                    return false;
                }
            }

            // Priority filter
            if (priorityFilter !== 'ALL' && t.priority !== priorityFilter) {
                return false;
            }

            // Team / Department filter
            if (teamFilter !== 'ALL') {
                const taskDept = (
                    t.department || 
                    projectUsers.find(u => u.username === t.username)?.department || 
                    users.find(u => u.username === t.username)?.department || 
                    ''
                ).trim();
                if (taskDept.toUpperCase() !== teamFilter.toUpperCase()) {
                    return false;
                }
            }

            // Employee / Assignee filter
            if (employeeFilter !== 'ALL') {
                const taskAssignee = t.username || t.assigneeId;
                const hasAssigneeInList = Array.isArray(t.assignedUsers) && t.assignedUsers.some(u => (u.username || u) === employeeFilter);
                if (taskAssignee !== employeeFilter && !hasAssigneeInList) {
                    return false;
                }
            }

            // Extra Product filter guard if tasks from multiple products were fetched
            if (productFilter !== 'ALL') {
                const pId = t.productId ? String(t.productId) : null;
                if (pId && pId !== String(productFilter)) return false;
            }

            return true;
        });
    }, [tasks, search, statusFilter, priorityFilter, teamFilter, employeeFilter, productFilter, projectUsers, users]);

    // Active filter count and chips
    const activeFilters = useMemo(() => {
        const filters = [];
        if (projectFilter !== 'ALL' && projectFilter !== String(projectId)) {
            const p = projectsList.find(x => String(x.id) === String(projectFilter));
            filters.push({
                key: 'project',
                label: `Project: ${p ? (p.projectName || p.name) : projectFilter}`,
                onRemove: () => setProjectFilter(String(projectId))
            });
        }
        if (productFilter !== 'ALL' && (!project?.productId || productFilter !== String(project.productId))) {
            const prod = productsList.find(x => String(x.id) === String(productFilter));
            filters.push({
                key: 'product',
                label: `Product: ${prod ? (prod.productname || prod.name) : productFilter}`,
                onRemove: () => setProductFilter(project?.productId ? String(project.productId) : 'ALL')
            });
        }
        if (statusFilter !== 'ALL') {
            const opt = STATUS_FILTER_OPTIONS.find(o => o.id === statusFilter);
            filters.push({
                key: 'status',
                label: `Status: ${opt ? opt.label : statusFilter}`,
                onRemove: () => setStatusFilter('ALL')
            });
        }
        if (priorityFilter !== 'ALL') {
            const opt = PRIORITY_FILTER_OPTIONS.find(o => o.id === priorityFilter);
            filters.push({
                key: 'priority',
                label: `Priority: ${opt ? opt.label : priorityFilter}`,
                onRemove: () => setPriorityFilter('ALL')
            });
        }
        if (teamFilter !== 'ALL') {
            filters.push({
                key: 'team',
                label: `Team: ${teamFilter}`,
                onRemove: () => setTeamFilter('ALL')
            });
        }
        if (employeeFilter !== 'ALL') {
            const empObj = uniqueEmployees.find(e => e.username === employeeFilter);
            filters.push({
                key: 'employee',
                label: `Assignee: ${empObj ? empObj.fullName : employeeFilter}`,
                onRemove: () => setEmployeeFilter('ALL')
            });
        }
        return filters;
    }, [projectFilter, productFilter, statusFilter, priorityFilter, teamFilter, employeeFilter, uniqueEmployees, projectsList, productsList, projectId, project]);

    const activeFilterCount = activeFilters.length;

    const clearAllFilters = () => {
        setSearch('');
        setProjectFilter(String(projectId));
        setProductFilter(project?.productId ? String(project.productId) : 'ALL');
        setStatusFilter('ALL');
        setPriorityFilter('ALL');
        setTeamFilter('ALL');
        setEmployeeFilter('ALL');
    };

    // Open Task details
    const openTask = (task) => {
        navigate(`/my-space/tasks/${task.taskId}`, { state: { task, from: `/ProjectDetails/${projectId}` } });
    };

    // Open Task Modal
    const handleOpenCreateTask = () => {
        setEditingTask(null);
        setIsTaskModalOpen(true);
    };

    const handleOpenEditTask = (task, e) => {
        if (e) e.stopPropagation();
        setOpenMenu(null);
        setEditingTask(task);
        setIsTaskModalOpen(true);
    };

    const handleSaveTask = async (taskPayload, attachmentsToUpload) => {
        try {
            let savedTask;
            if (editingTask && editingTask.taskId) {
                savedTask = await tasksApi.updateTask(editingTask.taskId, taskPayload);
                toast.success('Task updated successfully');
            } else {
                savedTask = await tasksApi.createTask(taskPayload);
                toast.success('Task created successfully');
            }

            // Upload attachments if any
            if (attachmentsToUpload && attachmentsToUpload.length > 0 && savedTask && savedTask.taskId) {
                for (const file of attachmentsToUpload) {
                    try {
                        await tasksApi.uploadAttachment(savedTask.taskId, file);
                    } catch (uploadErr) {
                        console.error('Failed to upload attachment:', uploadErr);
                    }
                }
            }

            setIsTaskModalOpen(false);
            setEditingTask(null);
            fetchTasks();
        } catch (err) {
            toast.error('Failed to save task.');
        }
    };

    // Delete Task
    const deleteTask = (taskId, e) => {
        if (e) e.stopPropagation();
        setOpenMenu(null);
        setConfirmModal({
            open: true,
            title: 'Delete Task',
            message: 'Are you sure you want to delete this task? This action cannot be undone.',
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, loading: true }));
                try {
                    await tasksApi.deleteTask(taskId);
                    toast.success('Task deleted');
                    fetchTasks();
                    setConfirmModal({ open: false, title: '', message: '', onConfirm: null, loading: false });
                } catch {
                    toast.error('Failed to delete task');
                    setConfirmModal(prev => ({ ...prev, loading: false }));
                }
            },
            loading: false
        });
    };

    // Status update from table row
    const handleUpdateStatus = async (e, task, newStatus) => {
        e.stopPropagation();
        if (!canChangeTaskStatus(task, currentUsername, role)) {
            if (task.taskStatus === 'COMPLETED') {
                toast.error('Only the task creator or admin can change the status of a completed task.');
            } else {
                toast.error('Only the task assignee or creator can edit this task.');
            }
            return;
        }
        try {
            await tasksApi.updateTaskStatus(task.taskId, newStatus);
            toast.success('Task status updated');
            fetchTasks();
        } catch {
            toast.error('Failed to update status');
        }
    };

    // Team assignment
    const assignUser = async () => {
        if (!assignUsername) return;
        try {
            await api.post('/project/assign', { projectId: parseInt(projectId), username: assignUsername });
            toast.success(`Assigned ${assignUsername}`);
            setAssignUsername('');
            fetchProjectUsers();
        } catch { toast.error('Failed to assign user'); }
    };

    const deassignUser = (username) => {
        setConfirmModal({
            open: true,
            title: 'Remove User',
            message: `Are you sure you want to remove ${username} from this project?`,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, loading: true }));
                try {
                    await api.post('/project/deassign', { projectId: parseInt(projectId), username });
                    toast.success(`Removed ${username}`);
                    fetchProjectUsers();
                    setConfirmModal({ open: false, title: '', message: '', onConfirm: null, loading: false });
                } catch {
                    toast.error('Failed to remove user');
                    setConfirmModal(prev => ({ ...prev, loading: false }));
                }
            },
            loading: false
        });
    };

    // Edit Project info
    const openEditProject = () => {
        setProjectFormData({
            projectCode: project.projectCode || '',
            projectName: project.projectName || '',
            description: project.description || '',
            status: project.status || 'PLANNING',
            clientName: project.clientName || '',
            projectManagerId: project.projectManagerId || '',
            budget: project.budget !== null && project.budget !== undefined ? String(project.budget) : '',
            startDate: project.startDate ? project.startDate.split('T')[0] : '',
            endDate: project.endDate ? project.endDate.split('T')[0] : '',
            productId: project.productId || project.product?.id || ''
        });
        setShowEditProjectModal(true);
    };

    const handleSaveProject = async () => {
        try {
            if (!projectFormData.projectName?.trim()) {
                toast.error('Project name is required');
                return;
            }
            if (!projectFormData.projectCode?.trim()) {
                toast.error('Project code is required');
                return;
            }
            const payload = {
                projectName: projectFormData.projectName.trim(),
                projectCode: projectFormData.projectCode.trim(),
                description: projectFormData.description?.trim() || null,
                clientName: projectFormData.clientName?.trim() || null,
                projectManagerId: projectFormData.projectManagerId || null,
                status: projectFormData.status || 'PLANNING',
                budget: projectFormData.budget !== '' && !isNaN(Number(projectFormData.budget)) ? Number(projectFormData.budget) : null,
                startDate: projectFormData.startDate || null,
                endDate: projectFormData.endDate || null,
                productId: projectFormData.productId ? Number(projectFormData.productId) : null
            };
            await api.put(`/project/${projectId}`, payload);
            toast.success('Project updated successfully');
            setShowEditProjectModal(false);
            fetchProject();
        } catch (err) {
            toast.error(err.response?.data?.message || err.response?.data || 'Failed to update project');
        }
    };

    const fmt = (d) => fmtDate(d) || null;

    return (
        <>
        <div className="max-w-7xl mx-auto space-y-5 animate-fade-in pb-20" onClick={() => setOpenMenu(null)}>
            
            <button 
                className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                onClick={() => navigate(-1)}
            >
                <FiArrowLeft /> Back
            </button>

            {error && <div className="p-4 bg-red-50 text-red-600 border border-red-200 rounded-xl flex items-center gap-3"><FiAlertCircle className="shrink-0" /> {error}</div>}

            {/* TOP UNIFIED PROJECT BANNER & META BAR */}
            {project && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 sm:p-6">
                    {/* Top Row: Title, Badges, and Action Button */}
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl shrink-0 mt-0.5">
                                <FiFolder />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight truncate">
                                        {project.projectName}
                                    </h1>
                                    <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                                        {project.projectCode}
                                    </span>
                                    {project.clientName && (
                                        <span className="px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                                            Client: <span className="font-semibold text-slate-800">{project.clientName}</span>
                                        </span>
                                    )}
                                    {project.status && (
                                        <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider ${
                                            project.status === 'ACTIVE' || project.status === 'IN_PROGRESS'
                                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                                        }`}>
                                            {project.status}
                                        </span>
                                    )}
                                </div>
                                {project.description && (
                                    <p className="text-xs sm:text-sm text-slate-600 mt-2 leading-relaxed max-w-4xl">
                                        {project.description}
                                    </p>
                                )}
                            </div>
                        </div>

                        {isAdmin && (
                            <button 
                                onClick={openEditProject}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors border border-slate-200 cursor-pointer self-start shrink-0 shadow-2xs"
                                title="Edit Project"
                            >
                                <FiEdit2 size={13} />
                                <span>Edit Details</span>
                            </button>
                        )}
                    </div>

                    {/* Bottom Row: Manager, Deadline, Task Count & Team Stack */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100 text-xs text-slate-600">
                        {/* Meta Chips */}
                        <div className="flex flex-wrap items-center gap-2.5">
                            <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
                                <FiUser className="text-slate-400" />
                                <span className="text-slate-500 font-medium">Manager:</span>
                                <span className="font-semibold text-slate-800">{project.projectManagerId || 'Unassigned'}</span>
                            </div>

                            {project.endDate && (
                                <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
                                    <FiCalendar className="text-slate-400" />
                                    <span className="text-slate-500 font-medium">Deadline:</span>
                                    <span className="font-semibold text-slate-800">{fmt(project.endDate)}</span>
                                </div>
                            )}

                            <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200">
                                <FiCheckSquare className="text-blue-500" />
                                <span className="text-slate-500 font-medium">Tasks:</span>
                                <span className="font-bold text-slate-800">{tasks.length}</span>
                            </div>
                        </div>

                        {/* Team Members Button & Stack */}
                        <div className="flex items-center gap-2.5">
                            {projectUsers.length > 0 && (
                                <div className="flex items-center -space-x-1.5 overflow-hidden">
                                    {projectUsers.slice(0, 4).map((u, i) => (
                                        <div 
                                            key={i} 
                                            className="inline-block h-7 w-7 rounded-full ring-2 ring-white bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-bold border border-slate-200 shadow-2xs"
                                            title={`${u.username} (${u.role?.replace('ROLE_', '') || 'Member'})`}
                                        >
                                            {u.username ? u.username.substring(0, 2).toUpperCase() : '?'}
                                        </div>
                                    ))}
                                    {projectUsers.length > 4 && (
                                        <div className="inline-block h-7 w-7 rounded-full ring-2 ring-white bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold border border-slate-300">
                                            +{projectUsers.length - 4}
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={() => setIsTeamDrawerOpen(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200/80 text-slate-700 transition-colors border border-slate-200 cursor-pointer shadow-2xs"
                            >
                                <FiUsers className="text-slate-500" />
                                <span>Team ({projectUsers.length})</span>
                                {isAdmin && <span className="text-[10px] text-blue-600 font-bold ml-0.5">• Manage</span>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* FULL-WIDTH MODERN TASKS SECTION */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[480px] w-full">
                
                {/* Section Header & Controls */}
                <div className="p-4 sm:p-5 border-b border-slate-200 space-y-3 bg-white">
                    
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h3 className="text-base font-bold text-slate-800">Project Tasks</h3>
                            <p className="text-xs text-slate-500">Deliverables and action items</p>
                        </div>

                        <div className="flex items-center gap-2">
                            <button 
                                onClick={handleOpenCreateTask}
                                className="inline-flex items-center gap-2 px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl transition-all shadow-xs cursor-pointer"
                            >
                                <FiPlus className="text-sm" />
                                <span>New Task</span>
                            </button>
                        </div>
                    </div>

                    {/* Toolbar: Search & Filters (Project & Product, NO Emp Filter) */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 pt-1">
                        
                        {/* Search Input */}
                        <div className="relative flex-1">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                            <input
                                type="text"
                                placeholder="Search tasks by title, deliverable, or assignee..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-9 pr-8 py-2 bg-slate-50 hover:bg-slate-100/80 focus:bg-white border border-slate-200 focus:border-blue-400 rounded-xl text-xs text-slate-700 placeholder-slate-400 focus:outline-none transition-all shadow-2xs"
                            />
                            {search && (
                                <button 
                                    onClick={() => setSearch('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                                >
                                    <FiX size={12} />
                                </button>
                            )}
                        </div>

                        {/* Filter Popover Button */}
                        <div className="relative">
                            <button
                                ref={filterBtnRef}
                                type="button"
                                onClick={() => setIsFilterOpen(prev => !prev)}
                                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all shadow-2xs cursor-pointer ${
                                    activeFilterCount > 0 || isFilterOpen
                                        ? 'bg-blue-50/70 border-blue-300 text-blue-700 ring-1 ring-blue-300/30'
                                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                <FiFilter className="text-xs" />
                                <span>Filters</span>
                                {activeFilterCount > 0 && (
                                    <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>

                            {/* Portal Filter Dropdown Popover */}
                            {isFilterOpen && createPortal(
                                <div
                                    ref={filterPopoverRef}
                                    style={{
                                        position: 'fixed',
                                        top: `${popoverPos.top}px`,
                                        right: `${popoverPos.right}px`,
                                        width: '320px',
                                        maxWidth: 'calc(100vw - 32px)',
                                        zIndex: 99999
                                    }}
                                    className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4 space-y-3 animate-fade-in"
                                >
                                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                                        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                                            <FiFilter className="text-blue-600" />
                                            <span>Task Filters</span>
                                        </div>
                                        {activeFilterCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={clearAllFilters}
                                                className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 cursor-pointer"
                                            >
                                                Reset All
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        {/* Project Filter */}
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                Project:
                                            </label>
                                            <select
                                                value={projectFilter}
                                                onChange={e => setProjectFilter(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                                            >
                                                <option value="ALL">All Projects</option>
                                                {projectsList.map(p => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.projectName || p.name || `Project #${p.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Product Filter */}
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                Product:
                                            </label>
                                            <select
                                                value={productFilter}
                                                onChange={e => setProductFilter(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                                            >
                                                <option value="ALL">All Products</option>
                                                {productsList.map(prod => (
                                                    <option key={prod.id} value={prod.id}>
                                                        {prod.productname || prod.name || `Product #${prod.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Team Filter */}
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                Team / Department:
                                            </label>
                                            <select
                                                value={teamFilter}
                                                onChange={e => {
                                                    setTeamFilter(e.target.value);
                                                    setEmployeeFilter('ALL');
                                                }}
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                                            >
                                                <option value="ALL">All Teams</option>
                                                {uniqueTeams.map(t => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Employee Filter */}
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                Employee / Assignee:
                                            </label>
                                            <select
                                                value={employeeFilter}
                                                onChange={e => setEmployeeFilter(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                                            >
                                                <option value="ALL">All Assignees</option>
                                                {uniqueEmployees.map(e => (
                                                    <option key={e.username} value={e.username}>
                                                        {e.fullName || e.username} (@{e.username})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Status Filter */}
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                Status:
                                            </label>
                                            <select
                                                value={statusFilter}
                                                onChange={e => setStatusFilter(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                                            >
                                                {STATUS_FILTER_OPTIONS.map(opt => (
                                                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Priority Filter */}
                                        <div>
                                            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                Priority:
                                            </label>
                                            <select
                                                value={priorityFilter}
                                                onChange={e => setPriorityFilter(e.target.value)}
                                                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs"
                                            >
                                                {PRIORITY_FILTER_OPTIONS.map(opt => (
                                                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="pt-2 border-t border-slate-100 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setIsFilterOpen(false)}
                                            className="w-full py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold shadow-xs cursor-pointer"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>

                    </div>

                    {/* Active Filter Chips / Pills */}
                    {activeFilterCount > 0 && (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            {activeFilters.map(filter => (
                                <span
                                    key={filter.key}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200/80 shadow-2xs"
                                >
                                    <span>{filter.label}</span>
                                    <button
                                        type="button"
                                        onClick={filter.onRemove}
                                        className="p-0.5 hover:bg-blue-100 rounded-full transition-colors cursor-pointer text-blue-600 hover:text-blue-900"
                                    >
                                        <FiX className="text-xs" />
                                    </button>
                                </span>
                            ))}

                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="text-xs text-slate-500 hover:text-slate-800 font-medium underline cursor-pointer ml-1"
                            >
                                Clear all
                            </button>
                        </div>
                    )}

                </div>

                {/* Table Column Headers (Full Width Grid) */}
                {!loading && !error && filteredTasks.length > 0 && (
                    <div className="hidden sm:grid sm:grid-cols-12 gap-3 lg:gap-4 px-6 py-3 bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider select-none">
                        <div className="sm:col-span-4">Task & Deliverable</div>
                        <div className="sm:col-span-2 text-center">Status</div>
                        <div className="sm:col-span-1 text-center">Priority</div>
                        <div className="sm:col-span-2 text-center">Assignee</div>
                        <div className="sm:col-span-1 text-center">Created By</div>
                        <div className="sm:col-span-2 text-center">Due Date</div>
                    </div>
                )}

                {/* Tasks Table Body */}
                <div className="p-0 flex-1">
                    {loading ? (
                        <div className="py-20 text-center text-slate-500 space-y-2">
                            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                            <p className="text-sm font-medium">Loading tasks...</p>
                        </div>
                    ) : filteredTasks.length === 0 ? (
                        <div className="py-20 text-center flex flex-col items-center">
                            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 border border-dashed border-slate-200 text-slate-400">
                                <TaskChecklistIcon size={26} />
                            </div>
                            <h3 className="text-base font-bold text-slate-800 mb-1">No Tasks Found</h3>
                            <p className="text-xs text-slate-500 max-w-sm mb-4">
                                {search || statusFilter !== 'ALL' || priorityFilter !== 'ALL'
                                    ? 'No tasks match your selected filter criteria. Try clearing filters.'
                                    : 'No tasks currently created in this project. Get started by adding a task.'}
                            </p>
                            <button 
                                onClick={handleOpenCreateTask} 
                                className="px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-xs cursor-pointer"
                            >
                                <FiPlus/> Add First Task
                            </button>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredTasks.map((task, idx) => {
                                const statusKey = STATUS_META[task.taskStatus] ? task.taskStatus : 'TO_DO';
                                const sm = STATUS_META[statusKey];

                                const prioKey = PRIORITY_META[task.priority] ? task.priority : 'MEDIUM';
                                const pm = PRIORITY_META[prioKey];

                                const isOverdue = Boolean(
                                    task.deadLine && 
                                    task.taskStatus !== 'COMPLETED' && 
                                    new Date(task.deadLine).setHours(0,0,0,0) < new Date().setHours(0,0,0,0)
                                );

                                const canEdit = canEditTask(task, currentUsername);
                        const canChangeStatus = canChangeTaskStatus(task, currentUsername, role);

                                return (
                                    <div
                                        key={task.taskId}
                                        onClick={() => openTask(task)}
                                        className={`p-3.5 sm:py-3.5 sm:px-6 sm:min-h-[4.75rem] transition-colors cursor-pointer group flex flex-col gap-2.5 sm:grid sm:grid-cols-12 sm:gap-3 lg:gap-4 sm:items-center ${
                                            idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                                        } hover:bg-blue-50/40`}
                                    >
                                        {/* Column 1: Task Title + Product Badge + Hover Description (sm:col-span-4) */}
                                        <div className="sm:col-span-4 min-w-0 flex flex-col justify-center h-full">
                                            {task.productName && (
                                                <div className="flex items-center gap-1.5 mb-1 shrink-0">
                                                    <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 truncate max-w-[140px]" title={`Product: ${task.productName}`}>
                                                        {task.productName}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="relative group/desc inline-block max-w-full">
                                                <h4 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors text-xs sm:text-sm truncate cursor-pointer">
                                                    {task.title}
                                                </h4>

                                                {task.description && task.description.trim() ? (
                                                    <div className="absolute left-0 top-full mt-1.5 hidden group-hover/desc:block z-40 w-72 sm:w-96 p-3 bg-white/95 backdrop-blur-md rounded-xl border border-slate-200/90 shadow-xl text-xs text-slate-600 font-normal leading-relaxed pointer-events-none transition-all">
                                                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center justify-between border-b border-slate-100 pb-1">
                                                            <span>Description</span>
                                                            <span className="text-[9px] text-slate-400 font-mono">TS-{task.taskId}</span>
                                                        </div>
                                                        <p className="whitespace-pre-wrap line-clamp-5 text-slate-700">{task.description}</p>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* Column 2: Status Dropdown (sm:col-span-2) */}
                                        <div className="hidden sm:flex sm:col-span-2 items-center justify-center" onClick={e => e.stopPropagation()}>
                                            <div className="relative inline-flex items-center">
                                                <select
                                                    value={statusKey}
                                                    onChange={e => handleUpdateStatus(e, task, e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    disabled={!canChangeStatus}
                                                    className={`appearance-none pl-2.5 pr-5 py-1 rounded-lg text-[11px] font-semibold border outline-none transition-all shadow-2xs ${sm.bg} ${sm.color} ${sm.border} ${
                                                        !canChangeStatus ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:opacity-90'
                                                    }`}
                                                    title={!canChangeStatus ? (task.taskStatus === 'COMPLETED' ? 'Completed tasks can only be reopened by the task creator or admin' : 'Status change restricted') : 'Click to change status'}
                                                >
                                                    <option value="TO_DO" className="bg-white text-slate-800 font-medium">To Do</option>
                                                    <option value="IN_PROGRESS" className="bg-white text-slate-800 font-medium">In Progress</option>
                                                    <option value="COMPLETED" className="bg-white text-slate-800 font-medium">Completed</option>
                                                    <option value="BLOCKED" className="bg-white text-slate-800 font-medium">Blocked</option>
                                                </select>
                                                <FiChevronDown className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] stroke-[2.5] ${sm.color} opacity-75`} />
                                            </div>
                                        </div>

                                        {/* Column 3: Priority Badge (sm:col-span-1) */}
                                        <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${pm.bg} ${pm.color} ${pm.border}`}>
                                                {pm.label}
                                            </span>
                                        </div>

                                        {/* Column 4: Assignee (sm:col-span-2) */}
                                        <div className="hidden sm:flex sm:col-span-2 items-center justify-center min-w-0 text-center px-1">
                                            <span 
                                                className={`text-xs font-semibold leading-tight line-clamp-2 break-words max-w-[125px] ${task.username ? 'text-slate-800' : 'text-slate-400 italic'}`} 
                                                title={task.assigneeName || task.username || 'Unassigned'}
                                            >
                                                {task.assigneeName || task.username || 'Unassigned'}
                                            </span>
                                        </div>

                                        {/* Column 5: Created By (sm:col-span-1) */}
                                        <div className="hidden sm:flex sm:col-span-1 items-center justify-center min-w-0 text-center px-1">
                                            <span 
                                                className={`text-xs font-medium leading-tight line-clamp-2 break-words max-w-[100px] ${task.createdBy ? 'text-slate-600' : 'text-slate-400 italic'}`} 
                                                title={task.createdBy || 'System'}
                                            >
                                                {task.createdBy ? String(task.createdBy).split('@')[0] : 'System'}
                                            </span>
                                        </div>

                                        {/* Column 6: Due Date (sm:col-span-2) */}
                                        <div className="hidden sm:flex sm:col-span-2 items-center justify-center relative h-full">
                                            <div className="flex flex-col items-center justify-center text-center">
                                                {task.deadLine ? (
                                                    <>
                                                        <span className={`text-xs whitespace-nowrap leading-tight ${
                                                            isOverdue ? 'text-rose-600 font-semibold' : 'text-slate-600 font-medium'
                                                        }`}>
                                                            {fmt(task.deadLine)}
                                                        </span>
                                                        {isOverdue && (
                                                            <span className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mt-0.5">
                                                                Overdue
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-slate-400 italic">No deadline</span>
                                                )}
                                            </div>

                                            {/* Options Menu (Edit / Delete) */}
                                            {(isAdmin || canEdit) && (
                                                <div className="absolute right-0 top-1/2 -translate-y-1/2" onClick={e => e.stopPropagation()}>
                                                    <button 
                                                        type="button"
                                                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 rounded-lg transition-colors cursor-pointer"
                                                        onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === task.taskId ? null : task.taskId); }}
                                                    >
                                                        <FiMoreVertical size={15} />
                                                    </button>
                                                    {openMenu === task.taskId && (
                                                        <div className="absolute right-0 mt-1 w-32 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 overflow-hidden">
                                                            {canEdit && (
                                                                <button 
                                                                    type="button"
                                                                    className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer" 
                                                                    onClick={e => handleOpenEditTask(task, e)}
                                                                >
                                                                    <FiEdit2 size={13} className="text-slate-400" /> Edit
                                                                </button>
                                                            )}
                                                            {isAdmin && (
                                                                <button 
                                                                    type="button"
                                                                    className="w-full text-left px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 flex items-center gap-2 cursor-pointer" 
                                                                    onClick={e => deleteTask(task.taskId, e)}
                                                                >
                                                                    <FiTrash2 size={13} className="text-rose-400" /> Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Mobile Info Row (< sm) */}
                                        <div className="flex sm:hidden flex-col gap-2 pt-2 border-t border-slate-100 text-xs">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                                    <div className="relative inline-flex items-center">
                                                        <select
                                                            value={statusKey}
                                                            onChange={e => handleUpdateStatus(e, task, e.target.value)}
                                                            onClick={e => e.stopPropagation()}
                                                            disabled={!canChangeStatus}
                                                            className={`appearance-none pl-2 pr-5 py-0.5 rounded text-[11px] font-semibold border outline-none ${sm.bg} ${sm.color} ${sm.border} ${
                                                                !canChangeStatus ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                                                            }`}
                                                            title={!canChangeStatus ? 'Status change restricted' : 'Click to change status'}
                                                        >
                                                            <option value="TO_DO" className="bg-white text-slate-800">To Do</option>
                                                            <option value="IN_PROGRESS" className="bg-white text-slate-800">In Progress</option>
                                                            <option value="COMPLETED" className="bg-white text-slate-800">Completed</option>
                                                            <option value="BLOCKED" className="bg-white text-slate-800">Blocked</option>
                                                        </select>
                                                        <FiChevronDown className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] stroke-[2.5] ${sm.color} opacity-75`} />
                                                    </div>
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border ${pm.bg} ${pm.color} ${pm.border}`}>
                                                        {pm.label}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <span className="text-xs truncate max-w-[110px] font-medium">{task.username || 'Unassigned'}</span>
                                                    {(isAdmin || canEdit) && (
                                                        <div className="relative" onClick={e => e.stopPropagation()}>
                                                            <button 
                                                                type="button"
                                                                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 rounded-lg transition-colors cursor-pointer"
                                                                onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === `mob-${task.taskId}` ? null : `mob-${task.taskId}`); }}
                                                            >
                                                                <FiMoreVertical size={14} />
                                                            </button>
                                                            {openMenu === `mob-${task.taskId}` && (
                                                                <div className="absolute right-0 mt-1 w-32 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1 overflow-hidden">
                                                                    {canEdit && (
                                                                        <button 
                                                                            type="button"
                                                                            className="w-full text-left px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer" 
                                                                            onClick={e => handleOpenEditTask(task, e)}
                                                                        >
                                                                            <FiEdit2 size={13} className="text-slate-400" /> Edit
                                                                        </button>
                                                                    )}
                                                                    {isAdmin && (
                                                                        <button 
                                                                            type="button"
                                                                            className="w-full text-left px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 flex items-center gap-2 cursor-pointer" 
                                                                            onClick={e => deleteTask(task.taskId, e)}
                                                                        >
                                                                            <FiTrash2 size={13} className="text-rose-400" /> Delete
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {task.deadLine && (
                                                <div className="flex items-center justify-between text-[11px] text-slate-400">
                                                    <span className="flex items-center gap-1">
                                                        <FiCalendar size={11} className={isOverdue ? 'text-rose-500' : 'text-slate-400'} />
                                                        <span className={isOverdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>
                                                            {fmt(task.deadLine)}
                                                        </span>
                                                    </span>
                                                    {isOverdue && (
                                                        <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.2 rounded border border-rose-200">
                                                            Overdue
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

            </div>
        </div>

        {/* Create / Edit Task Modal (Reusing CreateTaskModal with defaults) */}
        <CreateTaskModal
            isOpen={isTaskModalOpen}
            onClose={() => { setIsTaskModalOpen(false); setEditingTask(null); }}
            onSave={handleSaveTask}
            defaultProjectId={Number(projectId)}
            defaultProductId={project?.productId ? Number(project?.productId) : null}
            initialData={editingTask}
            isEdit={Boolean(editingTask)}
        />

        {/* Edit Project Modal (Portaled to document.body) */}
        {showEditProjectModal && createPortal(
            <div 
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-[99999] flex justify-end animate-fade-in"
                onClick={() => setShowEditProjectModal(false)}
            >
                <div 
                    className="bg-white w-full max-w-lg sm:max-w-xl shadow-2xl flex flex-col h-screen overflow-hidden transform transition-transform duration-300 translate-x-0 border-l border-slate-200"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                        <div>
                            <h3 className="text-base sm:text-lg font-bold text-slate-800">Edit Project</h3>
                            <p className="text-xs text-slate-500 mt-0.5">Update project details, timelines, manager, and product scope</p>
                        </div>
                        <button onClick={() => setShowEditProjectModal(false)} className="text-slate-400 hover:text-slate-600 bg-white shadow-xs border border-slate-200 rounded-full p-1.5 cursor-pointer"><FiX size={16} /></button>
                    </div>
                    <div className="p-6 space-y-4 overflow-y-auto flex-1">
                        {/* Row 1: Project Name & Code */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Project Name <span className="text-rose-500">*</span></label>
                                <input 
                                    type="text" 
                                    required
                                    value={projectFormData.projectName}
                                    onChange={(e) => setProjectFormData({...projectFormData, projectName: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs"
                                    placeholder="e.g. Website Redesign"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Project Code <span className="text-rose-500">*</span></label>
                                <input 
                                    type="text" 
                                    required
                                    value={projectFormData.projectCode}
                                    onChange={(e) => setProjectFormData({...projectFormData, projectCode: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs"
                                    placeholder="e.g. PRJ-001"
                                />
                            </div>
                        </div>

                        {/* Row 2: Status & Client Name */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Status</label>
                                <select 
                                    value={projectFormData.status}
                                    onChange={(e) => setProjectFormData({...projectFormData, status: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs cursor-pointer bg-white"
                                >
                                    <option value="PLANNING">Planning</option>
                                    <option value="ACTIVE">Active</option>
                                    <option value="IN_PROGRESS">In Progress</option>
                                    <option value="REVIEW">Review</option>
                                    <option value="COMPLETED">Completed</option>
                                    <option value="ON_HOLD">On Hold</option>
                                    <option value="CANCELLED">Cancelled</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Client Name</label>
                                <input 
                                    type="text" 
                                    value={projectFormData.clientName}
                                    onChange={(e) => setProjectFormData({...projectFormData, clientName: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs"
                                    placeholder="e.g. Pfizer, Internal"
                                />
                            </div>
                        </div>

                        {/* Row 3: Project Manager & Product Association */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Project Manager</label>
                                <select 
                                    value={projectFormData.projectManagerId}
                                    onChange={(e) => setProjectFormData({...projectFormData, projectManagerId: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs cursor-pointer bg-white"
                                >
                                    <option value="">-- No Manager Assigned --</option>
                                    {users.map(u => (
                                        <option key={u.id || u.username} value={u.username}>
                                            {u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username} (@{u.username}){u.department ? ` • ${u.department}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Associated Product</label>
                                <select 
                                    value={projectFormData.productId}
                                    onChange={(e) => setProjectFormData({...projectFormData, productId: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs cursor-pointer bg-white"
                                >
                                    <option value="">-- Standalone (No Product) --</option>
                                    {productsList.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.productname || p.name || `Product #${p.id}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Row 4: Budget, Start Date, End Date */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Budget ($)</label>
                                <input 
                                    type="number" 
                                    min="0"
                                    step="0.01"
                                    value={projectFormData.budget}
                                    onChange={(e) => setProjectFormData({...projectFormData, budget: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs"
                                    placeholder="e.g. 50000"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Start Date</label>
                                <input 
                                    type="date" 
                                    value={projectFormData.startDate}
                                    onChange={(e) => setProjectFormData({...projectFormData, startDate: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">End Date</label>
                                <input 
                                    type="date" 
                                    value={projectFormData.endDate}
                                    onChange={(e) => setProjectFormData({...projectFormData, endDate: e.target.value})}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs bg-white"
                                />
                            </div>
                        </div>

                        {/* Row 5: Description */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Description</label>
                            <textarea 
                                value={projectFormData.description}
                                onChange={(e) => setProjectFormData({...projectFormData, description: e.target.value})}
                                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 text-xs sm:text-sm font-medium text-slate-800 shadow-2xs resize-y"
                                placeholder="What is this project about? Scope, milestones, deliverables..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
                        <button 
                            type="button"
                            onClick={() => setShowEditProjectModal(false)}
                            className="px-4 py-2 text-slate-600 font-semibold hover:bg-slate-200 bg-slate-100 rounded-xl transition-colors cursor-pointer text-xs sm:text-sm"
                        >
                            Cancel
                        </button>
                        <button 
                            type="button"
                            onClick={handleSaveProject}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors cursor-pointer text-xs sm:text-sm shadow-xs"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )}

        {/* Team Members Slide-Over Drawer (Portaled to document.body) */}
        {isTeamDrawerOpen && createPortal(
            <div 
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-[99999] flex justify-end animate-fade-in"
                onClick={() => setIsTeamDrawerOpen(false)}
            >
                <div 
                    className="bg-white w-full max-w-md shadow-2xl flex flex-col h-screen overflow-hidden transform transition-transform duration-300 translate-x-0 border-l border-slate-200"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Drawer Header */}
                    <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <FiUsers className="text-blue-600" />
                                <span>Team Members</span>
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {projectUsers.length} active member{projectUsers.length === 1 ? '' : 's'} on {project?.projectName}
                            </p>
                        </div>
                        <button 
                            onClick={() => setIsTeamDrawerOpen(false)} 
                            className="text-slate-400 hover:text-slate-600 bg-white shadow-xs border border-slate-200 rounded-full p-1.5 cursor-pointer"
                        >
                            <FiX size={16} />
                        </button>
                    </div>

                    {/* Drawer Body */}
                    <div className="p-6 space-y-5 overflow-y-auto flex-1">
                        {/* Assign New Member Section (If Admin) */}
                        {isAdmin && (
                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2.5">
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                                    Assign Employee to Project
                                </label>
                                <div className="flex items-center gap-2">
                                    <select 
                                        value={assignUsername}
                                        onChange={e => setAssignUsername(e.target.value)}
                                        className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 shadow-2xs"
                                    >
                                        <option value="">Select Employee...</option>
                                        {users.filter(u => !projectUsers.some(pu => pu.username === u.username)).map(u => (
                                            <option key={u.id || u.username} value={u.username}>
                                                {u.fullName ? `${u.fullName} (${u.username})` : u.username}
                                            </option>
                                        ))}
                                    </select>
                                    <button 
                                        onClick={assignUser}
                                        disabled={!assignUsername}
                                        className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl transition-colors cursor-pointer flex items-center gap-1.5 text-xs font-semibold shadow-xs"
                                    >
                                        <FiUserPlus size={14} />
                                        <span>Add</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Current Member List */}
                        <div className="space-y-2">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                Assigned Team ({projectUsers.length})
                            </h4>

                            {projectUsers.length === 0 ? (
                                <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-xs text-slate-500">
                                    No members currently assigned to this project.
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden bg-white">
                                    {projectUsers.map((u, i) => (
                                        <div key={i} className="p-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0 border border-blue-100">
                                                    {u.username ? u.username.substring(0, 2).toUpperCase() : '?'}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-xs font-bold text-slate-800 truncate">
                                                        {u.fullName || u.username}
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 truncate">
                                                        @{u.username} • {u.role?.replace('ROLE_', '') || 'Member'}
                                                    </div>
                                                </div>
                                            </div>
                                            {isAdmin && (
                                                <button 
                                                    onClick={() => deassignUser(u.username)}
                                                    className="text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors p-1.5 cursor-pointer shrink-0"
                                                    title="Remove from project"
                                                >
                                                    <FiX size={15} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Drawer Footer */}
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
                        <button 
                            onClick={() => setIsTeamDrawerOpen(false)}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        )}

        <ConfirmModal
            isOpen={confirmModal.open}
            onClose={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null, loading: false })}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            confirmText="Confirm"
            loading={confirmModal.loading}
        />
        </>
    );
}
